import { ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { AppStateService } from 'src/app/modules/core/services/app-state.service';
import { BladeService } from '../../services/blade.service';
import { BladeComponent } from '../blade/blade.component';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-blade-container',
    templateUrl: './blade-container.component.html',
    styleUrls: ['./blade-container.component.scss'],
    imports: [BladeComponent, NgClass],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class BladeContainerComponent implements OnInit, OnDestroy {
    private _bladeService = inject(BladeService);
    private _stateService = inject(AppStateService);
    private readonly element = inject(ElementRef);
    private readonly zone = inject(NgZone);

    public outletNames = [];

    private observer: ResizeObserver;
    private _isClosingTransition = false;
    private _prevContainerWidth = 0;

    private readonly _onTransitionEnd = (e: TransitionEvent) => {
        if (e.propertyName === 'width' && this._isClosingTransition) {
            this._isClosingTransition = false;
            this.zone.run(() => {
                const el = this.element.nativeElement;
                this._stateService.setLeftTotalWidth(el.offsetLeft + el.offsetWidth);
            });
        }
    };

    constructor() {
        this.observer = new ResizeObserver(entries => {
            const currentWidth = (entries[0].target as HTMLElement).offsetWidth;
            if (currentWidth < this._prevContainerWidth) {
                // Width is decreasing (blade closing) — skip intermediate updates,
                // wait for transitionend to fire the final value.
                this._prevContainerWidth = currentWidth;
                this._isClosingTransition = true;
                return;
            }
            this._prevContainerWidth = currentWidth;
            this.zone.run(() => this.observe(entries));
        });
    }

    ngOnInit() {
        this.outletNames = this._bladeService.outletNames;
        this.observer.observe(this.element.nativeElement);

        const el = this.element.nativeElement;
        el.addEventListener('transitionend', this._onTransitionEnd);
        el.addEventListener('transitioncancel', this._onTransitionEnd);

        this._stateService.getPropertyState('leftTotalWidth').subscribe(() => {
            if (!this._isClosingTransition) {
                // Update total left width when state/context menu is opened
                this._stateService.setLeftTotalWidth(el.offsetLeft + el.offsetWidth);
            }
        });
    }

    ngOnDestroy() {
        this.observer.disconnect();
        const el = this.element.nativeElement;
        el.removeEventListener('transitionend', this._onTransitionEnd);
        el.removeEventListener('transitioncancel', this._onTransitionEnd);
    }

    private observe(entries: ResizeObserverEntry[]): void {

        // resize total left width when blade-container element changes
        const domSize = entries[0];
        if (domSize.target) {
            const el = domSize.target as HTMLElement;
            this._stateService.setLeftTotalWidth(el.offsetLeft + el.offsetWidth);
        }
    }
}
